import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {InformationObligation} from "../models/information-obligation.model";

/**
 * Serwis warstwy danych zarządzający Obowiązkiem Informacyjnym.
 * Obsługuje pobieranie szablonów, generowanie spersonalizowanych klauzul na podstawie danych
 * kliniki oraz zarządzanie cyklem życia (publikacją) indywidualnych dokumentów prawnych.
 */
@Injectable({
  providedIn: 'root'
})
export class InformationObligationService {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera aktualnie obowiązującą klauzulę informacyjną dla wskazanej placówki.
   *
   * @param vetcodeId Unikalny identyfikator kliniki.
   * @returns Observable emitujący zinstancjonowany obiekt klauzuli.
   */
  getInformationObligation(vetcodeId: number): Observable<InformationObligation> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/information-obligation/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new InformationObligation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wygenerowaną systemowo wersję obowiązku informacyjnego.
   *
   * @param vetcodeId Unikalny identyfikator kliniki.
   * @returns Observable emitujący wygenerowany obiekt klauzuli.
   */
  getGeneratedInformationObligation(vetcodeId: number): Observable<InformationObligation> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/generated-information-obligation/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new InformationObligation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera surowy, bazowy szablon obowiązku informacyjnego.
   *
   * @returns Observable emitujący treść szablonu jako ciąg znaków (string).
   */
  getTemplate(): Observable<string> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/dt/iob').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.value;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje obiekt obowiązku informacyjnego w bazie danych.
   *
   * @param informationObligation Obiekt reprezentujący zaktualizowaną klauzulę.
   * @returns Observable emitujący zaktualizowany obiekt zwrócony przez serwer.
   */
  addInformationObligation(informationObligation: InformationObligation): Observable<InformationObligation> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/information-obligation',informationObligation).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new InformationObligation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełną listę wszystkich klauzul informacyjnych wygenerowanych w ramach konta
   * nadrzędnego klienta.
   *
   * @param vClientUUID Unikalny identyfikator klienta.
   * @returns Observable emitujący tablicę zinstancjonowanych obiektów klauzul.
   */
  getInformationObligationsForClient(vClientUUID: string): Observable<InformationObligation[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/client-information-obligation/${vClientUUID}`).
    pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new InformationObligation(),[], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera indywidualną klauzulę przypisaną do danego klienta B2B.
   *
   * @param vClientUUID Unikalny identyfikator klienta B2B.
   * @returns Observable emitujący obiekt zaimportowanej klauzuli.
   */
  getIndividualInfOblForClient(vClientUUID: string): Observable<InformationObligation> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/iob/imported/${vClientUUID}`).
    pipe(take(1),map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new InformationObligation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Oznacza indywidualną klauzulę jako aktywną/publiczną, sprawiając, że staje się ona
   * widoczna dla pacjentów końcowych.
   *
   * @param iobUUID Unikalny identyfikator dokumentu.
   * @returns Observable emitujący opublikowany dokument.
   */
  publishIndividualInfObl(iobUUID: string): Observable<InformationObligation> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/iob/publish/${iobUUID}`).
    pipe(take(1),map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new InformationObligation(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza, czy w systemie centralnym pojawiła się nowa wersja szablonu prawnego,
   * która wymagałaby ponownego wygenerowania klauzul przez klienta.
   * Rzuca wyjątek, jeśli dane rejestrowe klienta są niekompletne (co uniemożliwia generację).
   *
   * @param vClientUUID Unikalny identyfikator klienta B2B.
   * @returns Observable emitujący treść nowego szablonu (jeśli istnieje).
   */
  checkIfNewTemplateExists(vClientUUID: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/iob-check/${vClientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.response) {
        return vetineoApiResponse.response.value;
      } else {
        if(vetineoApiResponse.errors && vetineoApiResponse.errors.main === 'WRONG_CLIENT_DATA'){
          throw new Error(vetineoApiResponse.errors.main);
        } else {
          return undefined;
        }
      }
    }));
  }

}
